"use client";

import { Item } from "@/lib/api/products";
import { UserProfile } from "@/data/profile/types";

interface ProductSchemaProps {
  item: Item;
  sellerProfile?: UserProfile | null;
}

export function ProductSchema({ item, sellerProfile }: ProductSchemaProps) {
  const title = (item as any).title || 
    `${(item as any).brand || ""} ${(item as any).model || ""}`.trim() || 
    "악기";
  
  const imageUrl = item.images && item.images.length > 0
    ? item.images[0]
    : "https://tone-market.com/TM_logo.png";

  const conditionMap: Record<string, string> = {
    "최상": "https://schema.org/NewCondition",
    "상": "https://schema.org/RefurbishedCondition",
    "중": "https://schema.org/UsedCondition",
    "하": "https://schema.org/DamagedCondition",
  };

  const condition = (item as any).condition || "중";
  const schemaCondition = conditionMap[condition] || "https://schema.org/UsedCondition";

  // SEO 최적화된 Product schema
  const productSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: title,
    description: (item as any).description || `${title} - ${item.price.toLocaleString()}원에 판매 중입니다. ${(item as any).category || "악기"} 카테고리, ${item.region || "전국"} 지역에서 거래 가능합니다.`,
    image: item.images && item.images.length > 0 ? item.images : [imageUrl],
    brand: {
      "@type": "Brand",
      name: (item as any).brand || "ToneMarket",
    },
    category: (item as any).category || "악기",
    offers: {
      "@type": "Offer",
      url: `https://tone-market.com/product/${item.id}`,
      priceCurrency: "KRW",
      price: item.price,
      priceValidUntil: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 90일 후
      availability: item.status === "active" || item.status === "available"
        ? "https://schema.org/InStock" 
        : "https://schema.org/OutOfStock",
      itemCondition: schemaCondition,
      seller: {
        "@type": sellerProfile ? "Person" : "Organization",
        name: sellerProfile?.nickname || "ToneMarket",
        ...(sellerProfile?.photoURL && {
          image: sellerProfile.photoURL,
        }),
      },
    },
    ...((item as any).averageRating && {
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: (item as any).averageRating.toString(),
        reviewCount: ((item as any).reviewCount || 0).toString(),
      },
    }),
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "홈",
        item: "https://tone-market.com",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "상품 목록",
        item: "https://tone-market.com/list",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: (item as any).category || "카테고리",
        item: `https://tone-market.com/list?category=${encodeURIComponent((item as any).category || "")}`,
      },
      {
        "@type": "ListItem",
        position: 4,
        name: title,
        item: `https://tone-market.com/product/${item.id}`,
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(productSchema),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(breadcrumbSchema),
        }}
      />
    </>
  );
}

